import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { AutoAwesome, Check, Close, ExpandMore, Refresh, Search } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { integrationApiService, MotzuMetafieldField, SpecCatalogEntry, StoreMetafieldDefinitionOption } from '../../../services/integrationApi';
import { tokens } from '../../../theme/tokens';
import { StatusChip } from '../../ui';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import type { SpecMetafieldRow } from '../../ShopifySpecMetafieldsSection';
import {
  metafieldTypeLabel,
  motzuKeySet,
  previewSpecWrite,
  suggestSpecLabel,
  visibleStoreDefinitions,
} from '../mappingModel';
import { SpecPickerPopover } from '../SpecPickerPopover';
import SpecCrosswalkControls from './SpecCrosswalkControls';
import { cantFillReason, isCantFillType, ProductSpecEntry, productValueForSpec, specEntriesFromSpecifications } from './specPreviewValues';

export type { SpecMetafieldRow };

/** Same props as `ShopifySpecMetafieldsSection`, so the shell can swap one for the other. */
export interface SpecMetafieldsV2ViewProps {
  integrationId: string;
  rows: SpecMetafieldRow[];
  onChange: (rows: SpecMetafieldRow[]) => void;
  onOpenProductFields?: () => void;
}

type SpecFilter = 'all' | 'suggested' | 'filled' | 'empty';

interface PreviewProduct {
  id: string;
  name: string;
  entries: ProductSpecEntry[];
}

/** Store metafield | spec | preview | clear; on a phone the spec and preview wrap under the metafield. */
const TABLE_COLUMNS = { xs: 'minmax(0,1fr) 32px', md: 'minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.8fr) 32px' };
const FULL_ROW_ON_PHONE = (order: number) => ({ gridColumn: { xs: '1 / -1', md: 'auto' }, order: { xs: order, md: 0 }, minWidth: 0 });
const PREVIEW_PRODUCT_LIMIT = 8;

const cardSx = {
  bgcolor: tokens.surface.card,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: `${tokens.radius.panel}px`,
  boxShadow: tokens.shadow.card,
} as const;

const definitionId = (row: { namespace: string; key: string }) => `${row.namespace}.${row.key}`;

const labelCaps = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: tokens.ink.muted,
} as const;

const SpecMetafieldsV2View: React.FC<SpecMetafieldsV2ViewProps> = ({ integrationId, rows, onChange, onOpenProductFields }) => {
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<SpecFilter>('all');
  const [search, setSearch] = useState('');
  const [cantOpen, setCantOpen] = useState(false);
  const [previewId, setPreviewId] = useState('');

  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integrationId],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId),
    staleTime: 5 * 60 * 1000,
  });
  // The spec catalog is cached 60 s on the server. The query key is shared with the mapping panels,
  // whose query functions never pass `refresh`, so an invalidate could refetch through one of them and
  // keep the server cache; fetch the key here with the refresh flag and write the result to the shared cache.
  const [rereading, setRereading] = useState(false);
  const reread = async () => {
    setRereading(true);
    try {
      await queryClient.fetchQuery({
        queryKey: ['shopify-spec-metafield-options', integrationId],
        queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId, { refresh: true }),
        staleTime: 0,
      });
    } catch {
      // The query surface shows the error state; nothing else to do here.
    } finally {
      setRereading(false);
    }
  };
  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });
  // The products this integration syncs, for "Preview with <product>". A store
  // without per-integration selections refuses the integration-scoped read, so
  // fall back to the store's own list.
  const productsQuery = useQuery({
    queryKey: ['shopify-spec-preview-products', integrationId],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PreviewProduct[]> => {
      let selection;
      try {
        selection = await apiService.getStoreSyncSelection(integrationId);
      } catch {
        selection = await apiService.getStoreSyncSelection();
      }
      const ids = (selection.selectedProductIds || []).slice(0, PREVIEW_PRODUCT_LIMIT);
      const settled = await Promise.allSettled(ids.map((id) => apiService.getCameraById(id)));
      const out: PreviewProduct[] = [];
      settled.forEach((result, index) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const product = result.value as { id?: string; name?: string; brand?: string; model?: string; specifications?: unknown };
        const name = product.name || `${product.brand || ''} ${product.model || ''}`.trim() || ids[index];
        out.push({ id: product.id || ids[index], name, entries: specEntriesFromSpecifications(product.specifications) });
      });
      return out;
    },
  });

  const motzuKeys = useMemo(() => motzuKeySet(fieldsQuery.data || []), [fieldsQuery.data]);
  const definitions = useMemo(
    () => visibleStoreDefinitions(optionsQuery.data?.definitions || [], motzuKeys),
    [optionsQuery.data, motzuKeys],
  );
  const fillable = useMemo(() => definitions.filter((definition) => !isCantFillType(definition.type)), [definitions]);
  const cantFill = useMemo(() => definitions.filter((definition) => isCantFillType(definition.type)), [definitions]);

  const catalog = useMemo<SpecCatalogEntry[]>(() => {
    const fromApi = optionsQuery.data?.specCatalog;
    if (fromApi?.length) return fromApi;
    return (optionsQuery.data?.specLabels || []).map((label) => ({ label, productCount: 0, sample: '', kind: 'text' as const }));
  }, [optionsQuery.data]);
  const hasCounts = Boolean(optionsQuery.data?.specCatalog?.length);

  const motzuFieldByPath = useMemo(() => {
    const map = new Map<string, MotzuMetafieldField>();
    for (const field of fieldsQuery.data || []) if (field.specPath) map.set(field.specPath, field);
    return map;
  }, [fieldsQuery.data]);
  const motzuFieldByLabel = useMemo(() => {
    const map = new Map<string, MotzuMetafieldField>();
    for (const field of fieldsQuery.data || []) map.set(field.name.trim().toLowerCase(), field);
    return map;
  }, [fieldsQuery.data]);

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) if (row.specLabel.trim()) map.set(definitionId(row), row.specLabel);
    return map;
  }, [rows]);

  const suggestions = useMemo(() => {
    const next = new Map<string, string>();
    for (const definition of fillable) {
      const id = definitionId(definition);
      if (labelById.get(id) || !definition.writable) continue;
      const label = suggestSpecLabel(definition, catalog);
      if (label) next.set(id, label);
    }
    return next;
  }, [fillable, labelById, catalog]);

  const filled = fillable.filter((definition) => Boolean(labelById.get(definitionId(definition)))).length;
  const pct = fillable.length > 0 ? Math.round((filled / fillable.length) * 100) : 0;

  const writeRows = (nextLabels: Map<string, string>) => {
    const visibleIds = new Set(definitions.map(definitionId));
    const kept = rows.filter((row) => {
      const id = definitionId(row);
      return !visibleIds.has(id) && !motzuKeys.has(id);
    });
    const mapped: SpecMetafieldRow[] = [];
    for (const definition of definitions) {
      const label = nextLabels.get(definitionId(definition))?.trim();
      if (!label) continue;
      mapped.push({ specLabel: label, namespace: definition.namespace, key: definition.key });
    }
    onChange([...mapped, ...kept]);
  };

  const setLabel = (definition: StoreMetafieldDefinitionOption, label: string) => {
    const next = new Map(labelById);
    if (label.trim()) next.set(definitionId(definition), label.trim());
    else next.delete(definitionId(definition));
    writeRows(next);
  };

  const acceptAll = () => {
    const next = new Map(labelById);
    suggestions.forEach((label, id) => next.set(id, label));
    writeRows(next);
    if (filter === 'suggested') setFilter('filled');
  };

  const products = productsQuery.data || [];
  const previewProduct = products.find((product) => product.id === previewId) || products[0] || null;

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fillable.filter((definition) => {
      const id = definitionId(definition);
      const isFilled = Boolean(labelById.get(id));
      if (filter === 'filled' && !isFilled) return false;
      if (filter === 'empty' && isFilled) return false;
      if (filter === 'suggested' && !suggestions.has(id)) return false;
      if (q && !`${definition.name} ${id}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [fillable, labelById, suggestions, filter, search]);

  const openDefinition = definitions.find((definition) => definitionId(definition) === openId) || null;

  const closePicker = () => {
    setAnchor(null);
    setOpenId(null);
  };

  const filters: Array<{ key: SpecFilter; label: string; count: number }> = [
    { key: 'all', label: 'All', count: fillable.length },
    { key: 'suggested', label: 'Suggested', count: suggestions.size },
    { key: 'filled', label: 'Filled', count: filled },
    { key: 'empty', label: 'Not filled', count: fillable.length - filled },
  ];

  const renderPreview = (definition: StoreMetafieldDefinitionOption, label: string) => {
    const muted = (text: string) => <Typography sx={{ fontSize: 12.5, color: tokens.ink.faint }}>{text}</Typography>;
    if (!label) return muted('Left as is in Shopify');
    const entry = catalog.find((item) => item.label === label);
    if (!entry) return muted('Left as is in Shopify');
    // With no products to switch between, the catalog's one sample stands in.
    const raw = previewProduct ? productValueForSpec(previewProduct.entries, entry) : entry.sample;
    const preview = previewSpecWrite(raw, definition.type);
    if (!preview) return muted('No value, left as is');
    return (
      <>
        <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: tokens.font.mono }} noWrap>{preview.value}</Typography>
        {preview.note && (
          <Typography sx={{ fontSize: 11.5, color: preview.lossy ? tokens.signal.caution.ink : tokens.ink.muted }}>{preview.note}</Typography>
        )}
        {!definition.writable && (
          <Typography sx={{ fontSize: 11.5, color: tokens.signal.caution.ink }}>Sync cannot fill this type</Typography>
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ ...cardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Spec → metafield</Typography>
            <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, maxWidth: 720, mt: 0.5 }}>
              Fill metafields your store already defined with a Motzu catalog spec. Lens mount → custom.mount gives a Sony a7 IV “Sony E”, so it shows under your Mount filter.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={optionsQuery.isFetching || rereading ? <CircularProgress size={14} /> : <Refresh />}
            onClick={() => { void reread(); }}
            sx={{ height: 36, alignSelf: 'flex-start', borderColor: tokens.line.default, borderRadius: `${tokens.radius.control}px`, fontWeight: 600, color: tokens.ink.body }}
          >
            Re-read definitions
          </Button>
        </Box>
        {optionsQuery.data?.specCrosswalk && (
          <SpecCrosswalkControls
            integrationId={integrationId}
            rows={rows}
            onChange={onChange}
            planLocked={optionsQuery.data.specCrosswalk.planLocked}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', fontSize: 13 }}>
            <Box component="span" data-testid="spec-filled-count" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {filled} of {fillable.length} fillable metafields filled
            </Box>
            <Box component="span" data-testid="spec-cant-count" sx={{ color: tokens.ink.muted }}>
              {cantFill.length} more are references a spec can&apos;t fill
            </Box>
            <Box component="span" sx={{ ml: { md: 'auto' }, color: tokens.ink.faint, fontSize: 12.5 }}>
              Read from Settings → Custom data → Products · the sync never creates definitions
            </Box>
          </Box>
          <Box
            role="progressbar"
            aria-label="Fillable metafields filled"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            sx={{ height: 4, borderRadius: '2px', bgcolor: tokens.surface.tint, overflow: 'hidden' }}
          >
            <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: tokens.signal.info.solid, transition: 'width .25s' }} />
          </Box>
        </Box>
      </Box>

      {(optionsQuery.isError || optionsQuery.data?.definitionsError) && (
        <Alert severity="warning">{optionsQuery.data?.definitionsError || 'Could not load the store’s metafield definitions.'}</Alert>
      )}

      {suggestions.size > 0 && (
        <Box sx={{ ...cardSx, bgcolor: tokens.signal.info.tint, borderColor: tokens.signal.info.border, boxShadow: 'none', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <AutoAwesome sx={{ fontSize: 18, color: tokens.signal.info.solid, flex: '0 0 auto' }} />
          <Typography sx={{ fontSize: 13, color: tokens.signal.info.ink, flex: '1 1 280px' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{suggestions.size} metafields have a spec with a matching name.</Box>
            {' '}Nothing is filled until you accept.
          </Typography>
          <Button
            size="small"
            onClick={() => {
              setFilter('suggested');
              setSearch('');
            }}
            sx={{ fontWeight: 600, color: tokens.signal.info.solid }}
          >
            Review
          </Button>
          <Button size="small" variant="contained" onClick={acceptAll} sx={{ height: 34, fontWeight: 600 }}>Accept all</Button>
        </Box>
      )}

      <Box sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 2.25, py: 1.75 }}>
          <Box role="group" aria-label="Filter metafields" sx={{ display: 'flex', gap: 0.5, p: '3px', bgcolor: tokens.surface.tint, borderRadius: `${tokens.radius.control}px` }}>
            {filters.map((item) => {
              const on = filter === item.key;
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFilter(item.key)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, height: 28, px: 1.375, border: 'none',
                    borderRadius: `${tokens.radius.small - 1}px`, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600,
                    bgcolor: on ? tokens.surface.card : 'transparent',
                    boxShadow: on ? tokens.shadow.card : 'none',
                    color: on ? tokens.ink.primary : tokens.ink.muted,
                  }}
                >
                  {item.label}
                  <Box component="span" sx={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums', color: item.key === 'suggested' && item.count > 0 ? tokens.signal.info.solid : tokens.ink.faint }}>
                    {item.count}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find a metafield"
            inputProps={{ 'aria-label': 'Find a metafield' }}
            sx={{ flex: '1 1 180px', minWidth: 160 }}
            slotProps={{ input: { startAdornment: (<InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment>) } }}
          />
          <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5, color: tokens.ink.muted }}>
            Preview with
            <Box
              component="select"
              aria-label="Preview with product"
              value={previewProduct?.id || ''}
              disabled={products.length === 0}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setPreviewId(event.target.value)}
              sx={{
                height: 34, px: 1, maxWidth: 240, border: '1px solid', borderColor: tokens.line.default,
                borderRadius: `${tokens.radius.small}px`, bgcolor: tokens.surface.card, font: 'inherit', fontSize: 13, color: tokens.ink.primary,
              }}
            >
              {products.length === 0 && <option value="">{productsQuery.isLoading ? 'Loading products' : 'No synced products'}</option>}
              {products.map((product) => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: TABLE_COLUMNS, gap: 1.75, px: 2.25, height: 34, alignItems: 'center', bgcolor: tokens.surface.sunken, borderTop: '1px solid', borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
          <Typography sx={labelCaps}>Store metafield</Typography>
          <Typography sx={labelCaps}>Filled from Motzu spec</Typography>
          <Typography sx={labelCaps} noWrap>{previewProduct ? `Preview · ${previewProduct.name}` : 'Preview'}</Typography>
          <span />
        </Box>

        {optionsQuery.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>}
        {!optionsQuery.isLoading && definitions.length === 0 && (
          <Typography sx={{ px: 2.25, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>
            No store metafield definitions yet. Create them in Shopify under Settings → Custom data → Products, then re-read definitions.
          </Typography>
        )}
        {definitions.length > 0 && visibleRows.length === 0 && (
          <Typography sx={{ px: 2.25, py: 3.5, textAlign: 'center', fontSize: 13, color: tokens.ink.muted }}>No metafield matches.</Typography>
        )}

        {visibleRows.map((definition) => {
          const id = definitionId(definition);
          const label = labelById.get(id) || '';
          const suggestion = !label ? suggestions.get(id) : undefined;
          const entry = catalog.find((item) => item.label === label);
          const motzuField = label
            ? (entry?.specPath ? motzuFieldByPath.get(entry.specPath) : undefined) || motzuFieldByLabel.get(label.trim().toLowerCase())
            : undefined;
          const isCustomSpec = Boolean(label) && (!motzuField || motzuField.tier === 'custom');
          return (
            <Box
              key={id}
              data-testid={`spec-row-${id}`}
              sx={{ display: 'grid', gridTemplateColumns: TABLE_COLUMNS, gap: 1.75, alignItems: 'center', px: 2.25, py: 1.375, borderBottom: '1px solid', borderColor: tokens.line.hairline }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{definition.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted, minWidth: 0 }} noWrap title={id}>{id}</Typography>
                  <Box sx={{ flex: '0 0 auto', px: 0.75, py: 0.125, borderRadius: `${tokens.radius.chip - 1}px`, bgcolor: tokens.surface.tint, fontSize: 10.5, fontWeight: 600, color: tokens.ink.bodyAlt }}>
                    {metafieldTypeLabel(definition.type)}
                  </Box>
                </Box>
              </Box>
              <Box sx={FULL_ROW_ON_PHONE(1)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Button
                    aria-label={`Spec for ${definition.name}`}
                    onClick={(event) => {
                      setAnchor(event.currentTarget);
                      setOpenId(id);
                    }}
                    sx={{
                      flex: '1 1 auto', minWidth: 0, justifyContent: 'space-between', height: 36, px: 1.25,
                      border: '1px solid', borderColor: openId === id ? tokens.signal.info.solid : tokens.line.default,
                      borderRadius: `${tokens.radius.small}px`, color: label ? tokens.ink.primary : tokens.ink.faint,
                      fontWeight: label ? 600 : 500, fontSize: 13,
                    }}
                    endIcon={<ExpandMore sx={{ fontSize: 16, color: tokens.ink.faint }} />}
                  >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label || 'Not filled'}</Box>
                  </Button>
                  {suggestion && (
                    <Button
                      size="small"
                      aria-label={`Use suggested spec ${suggestion} for ${definition.name}`}
                      startIcon={<Check sx={{ fontSize: 12 }} />}
                      onClick={() => setLabel(definition, suggestion)}
                      sx={{
                        flex: '0 0 auto', height: 28, whiteSpace: 'nowrap', border: '1px dashed', borderColor: tokens.accent.border,
                        bgcolor: tokens.accent.tint, color: tokens.accent.hover, fontSize: 12, fontWeight: 600,
                        '&:hover': { bgcolor: tokens.accent.tintStrong },
                      }}
                    >
                      {suggestion}
                    </Button>
                  )}
                </Box>
                {label && (
                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <StatusChip label={t(isCustomSpec ? 'integrations.specTier.custom' : 'integrations.specTier.canonical')} tone={isCustomSpec ? 'caution' : 'neutral'} />
                    <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
                      {motzuField && motzuField.tier !== 'custom'
                        ? motzuField.description
                        : t(motzuField ? 'integrations.specTier.customHint' : 'integrations.specTier.vendorSpecHint')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={FULL_ROW_ON_PHONE(2)}>{renderPreview(definition, label)}</Box>
              {label ? (
                <IconButton size="small" title="Stop filling this metafield" aria-label={`Stop filling ${definition.name}`} onClick={() => setLabel(definition, '')}>
                  <Close sx={{ fontSize: 16, color: tokens.ink.muted }} />
                </IconButton>
              ) : (
                <span />
              )}
            </Box>
          );
        })}

        <Box
          component="button"
          type="button"
          aria-expanded={cantOpen}
          onClick={() => setCantOpen((open) => !open)}
          sx={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 1.625, border: 'none',
            borderBottom: '1px solid', borderColor: tokens.line.hairline, bgcolor: tokens.surface.card, textAlign: 'left',
            cursor: 'pointer', font: 'inherit', '&:hover': { bgcolor: tokens.surface.sunken },
          }}
        >
          <ExpandMore sx={{ fontSize: 16, color: tokens.ink.muted, transform: cantOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.body }}>
            {cantFill.length} metafields can&apos;t be filled from a spec
          </Box>
          <Box component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>
            They point to other Shopify objects or hold rich text.
          </Box>
        </Box>
        {cantOpen &&
          cantFill.map((definition) => (
            <Box
              key={definitionId(definition)}
              data-testid={`spec-cant-${definitionId(definition)}`}
              sx={{
                display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1.95fr)' }, gap: 1.75,
                alignItems: 'center', pl: { xs: 2.25, md: 5 }, pr: 2.25, py: 1.25, borderBottom: '1px solid',
                borderColor: tokens.line.hairline, bgcolor: tokens.surface.subtle, opacity: 0.85,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.bodyAlt }} noWrap>{definition.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.faint, minWidth: 0 }} noWrap>{definitionId(definition)}</Typography>
                  <Box sx={{ flex: '0 0 auto', px: 0.75, py: 0.125, borderRadius: `${tokens.radius.chip - 1}px`, bgcolor: tokens.surface.tint, fontSize: 10.5, fontWeight: 600, color: tokens.ink.muted }}>
                    {metafieldTypeLabel(definition.type)}
                  </Box>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{cantFillReason(definition.type)}</Typography>
            </Box>
          ))}

        <Typography sx={{ px: 2.25, py: 1.5, fontSize: 12.5, color: tokens.ink.muted }}>
          Metafield not listed? Create it in Shopify under Settings → Custom data → Products, then re-read definitions. Motzu&apos;s own fields live in{' '}
          {onOpenProductFields ? (
            <Button onClick={onOpenProductFields} sx={{ p: 0, minWidth: 0, fontWeight: 700, fontSize: 'inherit', verticalAlign: 'baseline' }}>Product Fields</Button>
          ) : (
            'Product Fields'
          )}
          .
        </Typography>
      </Box>

      <SpecPickerPopover
        open={Boolean(openDefinition)}
        anchorEl={anchor}
        onClose={closePicker}
        catalog={catalog}
        hasCounts={hasCounts}
        definitionType={openDefinition?.type ?? null}
        currentLabel={openDefinition ? labelById.get(definitionId(openDefinition)) : undefined}
        syncedProducts={optionsQuery.data?.syncedProducts}
        onPick={(label) => {
          if (openDefinition) setLabel(openDefinition, label);
          closePicker();
        }}
      />
    </Box>
  );
};

export default SpecMetafieldsV2View;
